import { convertBlobToBase64 } from '@/utils/file';
import { Col, Form, Row } from 'antd';
import { cloneDeep } from 'es-toolkit/object';
import { useEffect, useMemo } from 'react';
import { Fragment } from 'react';
import Files from 'react-files';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { routeFileSelector } from '../../../../../../utilz/i18n';
import Button from '../../../../../components/Buttons/Button';
import SimpleFileList from '../../../../../components/Cards/SimpleFileList';
import FormTextArea from '../../../../../components/Inputs/FormTextArea';
import HiddenFormInput from '../../../../../components/Inputs/HiddenFormInput';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import Paper from '../../../../../components/Paper/Paper';
import PreClaimDocumentTypeSelector from '../../../../../components/Selectors/PreClaimDocumentTypeSelector';
import SaveCancelButtons from '../../../../Shipment/components/SaveCancelButtons';

const LADClaimsFilesCard = ({
  editMode = false,
  isLastItem = false,
  shadowVisible = true,
  index = 0,
  fileDetails,
  setEditMode,
  handleCancel,
  handleSubmit,
  handleDelete,
  fileParamName = 'fileContent',
}) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [form] = Form.useForm();
  const fileList = Form.useWatch('fileList', form);

  useEffect(() => {
    if (fileDetails) {
      form.setFieldsValue(fileDetails);
    }
  }, [fileDetails]);

  const convertFileToBase64 = (file, defaultData) =>
    new Promise((resolve, reject) =>
      (async () => {
        try {
          const base64 = await convertBlobToBase64(file);
          const data = {
            dirtyFlag: defaultData.dirtyFlag,
            [fileParamName]: base64.split(',')[1],
            sequence: defaultData.sequence,
            fileName: file.name,
          };
          resolve(data);
        } catch (error) {
          reject(error);
          console.log(error);
        }
      })(),
    );

  const handleChooseFile = async (selectedFiles) => {
    try {
      let fileListL = cloneDeep(fileList ? fileList : []);
      let newFiles = selectedFiles.map((row) => convertFileToBase64(row, { dirtyFlag: 'i', sequence: 0 }));
      newFiles = await Promise.all(newFiles);
      fileListL = [...fileListL, ...newFiles];
      form.setFieldValue('fileList', fileListL);
    } catch (error) {
      console.log(error);
    }
  };

  const handleFileDelete = (index) => () => {
    const fileListL = cloneDeep(fileList ? fileList : []);
    fileListL[index].dirtyFlag = 'D';
    form.setFieldValue('fileList', fileListL);
  };

  return (
    <Paper className={`border-muted ${!isLastItem ? 'mb-16' : ''} ${shadowVisible ? '' : 'shadow-none'}`}>
      <Form onFinish={(data) => handleSubmit(data, index)} initialValues={fileDetails} form={form}>
        <div className="border-bottom-muted pt-8 pb-10 px-16">
          <Row justify="space-between align-items-center">
            <Col className="flex-1" align="middle">
              <Row gutter={10} align="middle">
                <Col>
                  {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
                  <label className="mandatory-label">
                    File Name: <span className="mandatory-field">*</span>
                  </label>
                </Col>
                <Col>
                  {editMode ? (
                    <Files
                      className="files-dropzone"
                      onChange={handleChooseFile}
                      // onError={handleFileError}
                      // accepts={["image/*", "document/*"]}
                      accepts={['.xlsx', '.xls', 'image/*', '.doc', '.docx', '.ppt', '.pptx', '.txt', '.pdf']}
                      multiple
                      // maxFileSize={10000000}
                      minFileSize={0}
                      clickable={editMode}
                    >
                      <Button disable tabIndex={(index + 1) * 2 + 30000 + 1}>
                        {t('translation:default.documentManager.chooseFile')}
                      </Button>
                    </Files>
                  ) : (
                    <></>
                  )}

                  <HiddenFormInput
                    name="fileList"
                    rules={[
                      {
                        required: true,
                        message: t('translation:default.documentManager.chooseFileMessage'),
                      },
                    ]}
                  />
                </Col>
              </Row>
            </Col>

            <Col>
              <SaveCancelButtons
                editMode={editMode}
                lightMode={false}
                // shipmentEditStatus={shipmentEditStatus}
                setEditMode={(val) => setEditMode(index, val)}
                handleRemove={handleDelete(index)}
                isNew={fileDetails.isNew}
                idSuffix={`product-details-${index + 1}`}
                isModified
                onCancel={handleCancel(index, fileDetails.isNew)}
              />
            </Col>
          </Row>
        </div>
        <div className="px-15 pt-10">
          <Row gutter={15}>
            {/* {fileList?.length > 0 ? ( */}
            <Col sm={8} className="pb-10">
              <PrimaryLabel className="mb-5">Selected Files:</PrimaryLabel>
              <div>
                {fileList?.length > 0 &&
                  fileList.map((file, index) =>
                    file?.dirtyFlag !== 'D' ? (
                      <SimpleFileList
                        index={index}
                        key={file.id}
                        data={file}
                        handleDelete={handleFileDelete(index)}
                        showDeleteButton={editMode}
                      />
                    ) : (
                      <Fragment key={file.id} />
                    ),
                  )}
              </div>
            </Col>
            {/* ) : (
              <></>
            )} */}

            <Col sm={8}>
              <PreClaimDocumentTypeSelector
                label="Document Type"
                placeholder="Select Document Type"
                editMode={editMode}
                name="documentType"
                rules={[{ required: true, message: 'Please choose a Document Type' }]}
                tabIndex={(index + 1) * 2 + 30000 + 2}
              />
            </Col>
            <Col sm={8}>
              <FormTextArea
                tabIndex={(index + 1) * 2 + 30000 + 3}
                label={t('translation:claims.comments')}
                placeholder={t('translation:claims.enterComments')}
                editMode={editMode}
                {...(editMode ? { rows: 3 } : {})}
                name="fileComments"
                maxLength={640}
              />
            </Col>
          </Row>
        </div>
        <HiddenFormInput name="sequence" />
        <HiddenFormInput name="isNew" />
        <HiddenFormInput name="editMode" />
        <HiddenFormInput name="claimKey" />
        <HiddenFormInput name="dirtyFlag" />
      </Form>
    </Paper>
  );
};

export default LADClaimsFilesCard;
